import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as dms from '../../../dms'
import * as roomTypes from '../../room-types'
import * as records from '../records'

import * as gql from './gql'
import type { TypeParams } from './types'

/** Space cluster assigned records API */
@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(dms.schema.Feature)

  /** @see {@link roomTypes.Collection} */
  private readonly _roomTypesEc = ngCore.inject(roomTypes.Collection)

  /** @inheritdoc */
  public list = ec.models.Api.list.using.everything<TypeParams>(
    (context, request) => {
      if (!context) {
        return rxjs.of([])
      }

      const spaceFilter = request?.filter?.additionalFilters?.spaceFilter

      return records.gql.queries.spaceRecords.executor({
        ...context,
        filter: spaceFilter ? { spaceFilter } : undefined,
        sort: request?.sort && {
          by: request.sort.by,
          descending: request.sort.descending,
        },
      })
    },
  )

  /** @inheritdoc */
  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    if (!context) {
      return rxjs.of(undefined)
    }
    return gql.mutations.removeSpaceRecordFromCluster.executor({
      ...context,
      spaceRecordId: TypeAsserter.validate(
        id,
        ents.extra.space.Record.Id.assertIsId,
      ),
    })
  }

  /** @inheritdoc */
  public create: ec.models.Api<TypeParams>['create'] = (
    context,
    { spaceRecordIds },
  ) => {
    if (!context) {
      return rxjs.of(undefined)
    }

    return gql.mutations.addSpaceRecordsToCluster.executor({
      ...context,
      spaceRecordIds,
    })
  }

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] =
    context => {
      if (!context) {
        return rxjs.of([])
      }

      return rxjs
        .combineLatest({
          schema: this._schemaFeature.selectors.schema.asObservable,
          spaceRoomTypesLookup:
            this._roomTypesEc.plugins.cachePlugin.feature.selectors.lookup
              .asObservable,
        })
        .pipe(
          rxjs.filter(
            ({ schema }) => !!schema?.tryGetLeafEntityOf('dim::!space'),
          ),
          rxjs.map(({ schema, spaceRoomTypesLookup }) => {
            const spaceLeafEntity = schema.getLeafEntityOf('dim::!space')
            const ancestors = schema.getAncestorsOf(spaceLeafEntity.id)

            const ancestorAttributes: ec.models.Attribute<TypeParams>[] =
              ancestors.map(({ name, id }) => ({
                getValueFrom: record => record.ancestors[id]?.name,
                id,
                isSortable: true,
                name,
              }))

            return [
              ...ancestorAttributes,
              ec.models.Attribute.fromKey<TypeParams>('name', {
                isSortable: true,
                name: 'Room',
              }),
              {
                getValueFrom: record =>
                  spaceRoomTypesLookup?.[record.roomTypeId]?.name,
                id: 'roomType',
                isSortable: true,
                name: 'Room Type',
              },
            ]
          }),
        )
    }
}
